import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText, Users, ShoppingCart, Megaphone } from "lucide-react";
import { InviteColleagueDialog } from "@/components/InviteColleagueDialog";
import { GibletChatDialogEnhanced } from "@/components/chat/GibletChatDialogEnhanced";
import sprintButton from "@/assets/icons/sprint-hands.png";

export const NavbarActionButtons = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [gibletChatOpen, setGibletChatOpen] = useState(false);
  const [selectedCallType, setSelectedCallType] = useState<'solution' | 'knowledge' | 'destruction' | 'start-call-flow' | null>(null);

  // Determine channel context from current route
  const getChannelContext = () => {
    if (location.pathname.includes('curt')) {
      return { channelId: 'curt-channel-id', channelName: 'CURT' };
    } else if (location.pathname.includes('usa')) {
      return { channelId: 'usa-channel-id', channelName: 'US Federal Government' };
    } else if (location.pathname.includes('oil-gas')) {
      return { channelId: 'oil-gas-channel-id', channelName: 'Oil & Gas' };
    }
    return { channelId: 'ispai-channel-id', channelName: 'iSPAI' };
  };

  const { channelId, channelName } = getChannelContext();

  const handleCallTypeSelect = (type: 'solution' | 'knowledge' | 'destruction') => {
    setSelectedCallType(type);
    setGibletChatOpen(true);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => navigate("/creators", { state: { reset: true } })}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Create a new knowledge base"
            >
              <FileText className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Create a new knowledge base</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => navigate("/prompt-architecture-sprint")}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Launch a Sprint"
            >
              <img src={sprintButton} alt="Launch a Sprint" className="h-7 w-7 object-contain" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Collaborate with your network to advance prompts that build better knowledge bases for AI</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                setSelectedCallType('start-call-flow');
                setGibletChatOpen(true);
              }}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Initiate a Call"
            >
              <Megaphone className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Create a Call for Solutions, Knowledge, or Destruction</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => setInviteDialogOpen(true)}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Invite a colleague"
            >
              <Users className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Invite a colleague to your knowledge network or channel</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => navigate("/ispai-solutions")}
              size="icon"
              className="h-10 w-10 bg-green-700 hover:bg-green-700 text-white"
              aria-label="Go to Marketplace"
            >
              <ShoppingCart className="h-6 w-6" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Go to the Marketplace to consume KBs or Solutions that have been minted</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <InviteColleagueDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        channelId={channelId}
        channelName={channelName}
      />

      <GibletChatDialogEnhanced
        open={gibletChatOpen}
        onOpenChange={setGibletChatOpen}
        initialCallType={selectedCallType}
      />
    </>
  );
};
